<template>
  <v-container class="user" grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <h1 class="primary--text mb-4">Account Settings</h1>
        <app-alert class="mt-4 mb-0" type="error" :message="error" @dismissed="error = false"/>
        <app-alert
          class="mt-4 mb-0"
          type="success"
          :message="userUpdated"
          @dismissed="userUpdated = false"
        />
      </v-flex>

      <v-flex xs12 md7>
        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span>Edit Your Profile</span>
          </v-card-title>
          <v-divider></v-divider>

          <v-container class="px-4" grid-list-lg>
            <v-form ref="form" @submit.prevent="updateUser">
              <v-layout justify-space-between row wrap>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Name"
                    type="text"
                    v-model="user.name"
                    :rules="[rules.isRequired]"
                    @input="canSave = true"
                    :disabled="!user"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field
                    label="Email"
                    type="text"
                    v-model="user.email"
                    @input="canSave = true"
                    disabled
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-select
                    label="Status"
                    v-model="user.status"
                    :items="statusOptions"
                    @input="canSave = true"
                    disabled
                    required
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6 v-if="user.role && roleOptions.length">
                  <v-select
                    label="Role"
                    v-model="user.role"
                    :items="roleOptions"
                    @input="canSave = true"
                    :disabled="!user || currentUser._id === user._id"
                    required
                  ></v-select>
                </v-flex>
              </v-layout>

              <v-layout class="mt-3">
                <v-btn
                  depressed
                  color="accent"
                  type="submit"
                  :disabled="!user || !canSave || isSaving"
                >
                  Save
                  <v-progress-circular
                    class="ml-3"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="isSaving"
                  ></v-progress-circular>
                </v-btn>
              </v-layout>
            </v-form>
          </v-container>
        </v-card>
      </v-flex>

      <!-- Change user password -->
      <v-flex xs12 md5>
        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span>Change Password</span>
          </v-card-title>
          <v-divider></v-divider>

          <v-container class="px-4" grid-list-lg>
            <v-form ref="passwordForm" @submit.prevent="updatePassword">
              <v-layout justify-space-between row wrap>
                <v-flex xs12>
                  <v-text-field
                    label="Old Password"
                    type="password"
                    v-model="password.hash_old"
                    :rules="[rules.isRequired]"
                    :disabled="!user"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field
                    label="New Password"
                    type="password"
                    v-model="password.hash_new"
                    :rules="[rules.isRequired]"
                    :disabled="!user"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field
                    label="Confirm Password"
                    type="password"
                    v-model="password.c_hash_new"
                    @input="canSavePassword = true"
                    :rules="[rules.isRequired, matchedPasswords]"
                    :disabled="!user"
                    required
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout class="mt-3">
                <v-btn
                  depressed
                  color="accent"
                  type="submit"
                  :disabled="!user || !canSavePassword || isSavingPassword"
                >
                  Save
                  <v-progress-circular
                    class="ml-3"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="isSavingPassword"
                  ></v-progress-circular>
                </v-btn>
              </v-layout>
            </v-form>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { USER_UPDATE } from '@/store/actions/user'
import rolesData from '@/data/roles'
import { statusOptions, roleOptionsEdit } from '@/data/options'
import { setFormData } from '@/utils/http'

export default {
  name: 'Profile',
  created () {
    this.user = Object.assign({}, this.currentUser)
    this.password = {
      username: this.user.email,
      hash_old: '',
      hash_new: '',
      c_hash_new: ''
    }
  },
  data () {
    return {
      user: null,
      password: null,
      canSave: false,
      canSavePassword: false,
      error: false,
      isSaving: false,
      isSavingPassword: false,
      userUpdated: false,
      statusOptions: statusOptions,
      rules: {
        isRequired: value => (value && value.trim() === '') ? 'This field is required' : true,
        email: value => (value && !(/\S+@\S+\.\S+/).test(value.trim())) ? 'Enter a valid email' : true
      }
    }
  },
  computed: {
    matchedPasswords () {
      return this.password.hash_new !== this.password.c_hash_new ? 'Passwords do not match' : true
    },
    roleOptions () {
      return roleOptionsEdit.filter(item => {
        return this.$can(item.can)
      })
    },
    ...mapGetters(['currentUser'])
  },
  methods: {
    updateUser () {
      if (!this.$refs.form.validate()) return

      this.error = false
      this.isSaving = true
      this.userUpdated = false

      const updatedUser = Object.assign({}, this.user)
      if (updatedUser.role === rolesData.admin) delete updatedUser.role // removes role for admin
      delete updatedUser.created // removes the created timestamp
      delete updatedUser.modified // removes the modified timestamp
      
      this.$store.dispatch(USER_UPDATE, { 
          isAdmin: updatedUser.role === undefined,
          isCurrentUser: true,
          user: updatedUser
        })
        .then(() => {
          this.userUpdated = 'Your profile has been successfully updated.'
        })
        .finally(() => {
          this.error = false
          this.canSave = false
          this.isSaving = false
        })
    },
    updatePassword () {
      if (!this.$refs.passwordForm.validate()) return

      this.error = false
      this.isSavingPassword = true
      this.userUpdated = false

      const updatedPassword = Object.assign({}, this.password)
      delete updatedPassword.c_hash_new // removes the confirm password
      
      const params = setFormData(updatedPassword)
      this.$http.post('user/change_auth', params)
        .then(({ data: response }) => {
          if (response.code === 200) {
            this.userUpdated = 'Your password has been successfully changed.'
            this.$refs.passwordForm.reset()
          }
          else {
            this.error = response.code === 400 ? 'Old password incorrect' : response.message
          }
        })
        .finally(() => {
          this.canSave = false
          this.isSavingPassword = false
        })
    }
  }
}
</script>
